// core/modals/expenses/ViewExpense.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { 
    X, 
    DollarSign, 
    Calendar, 
    FileText, 
    User, 
    Bookmark, 
    CreditCard, 
    Hash,
    File,
    Download,
    Eye
} from 'feather-icons-react/build/IconComponents';
import { expenseActions } from '../../action/expenseActions';

const ViewExpense = ({ expense, onClose }) => {
    
    // Get payment method icon
    const getPaymentMethodIcon = (method) => {
        const icons = {
            'cash': '💰',
            'bank_transfer': '🏦',
            'cheque': '📝',
            'credit_card': '💳',
            'upi': '📱'
        };
        return icons[method] || '💵';
    };
    
    // Get category icon
    const getCategoryIcon = (iconName) => {
        const icons = {
            'Briefcase': '💼',
            'Home': '🏠',
            'Zap': '⚡',
            'Users': '👥',
            'MapPin': '📍',
            'TrendingUp': '📈',
            'Tool': '🔧',
            'Folder': '📁'
        };
        return icons[iconName] || '📂';
    };
    
    // Handle view attachment
    const handleViewAttachment = () => {
        if (expense.bill_attachment) {
            window.open('/' + expense.bill_attachment, '_blank');
        }
    };
    
    // Handle download attachment
    const handleDownloadAttachment = () => {
        if (expense.bill_attachment) {
            const link = document.createElement('a');
            link.href = '/' + expense.bill_attachment;
            link.download = expense.bill_attachment.split('/').pop();
            link.click();
        }
    };
    
    return (
        <Modal show={true} onHide={onClose} size="lg" centered>
            <Modal.Header className="border-0 pb-0">
                <Modal.Title>
                    <h5 className="mb-0">Expense Details</h5>
                </Modal.Title>
                <button type="button" className="btn-close" onClick={onClose}>
                    <X size={18} />
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="card border-0">
                    <div className="card-header bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-0">Expense #{expense.expense_no}</h6>
                                <small className="text-muted">
                                    Created on {new Date(expense.created_at).toLocaleString()}
                                </small>
                            </div>
                            <span className={`badge ${expenseActions.getStatusBadge(expense.status)}`}>
                                {expense.status?.toUpperCase()}
                            </span>
                        </div>
                    </div>
                    
                    <div className="card-body">
                        <div className="row">
                            {/* Date & Amount */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-start">
                                    <div className="bg-light p-2 rounded me-3">
                                        <Calendar size={20} className="text-primary" />
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Expense Date</small>
                                        <strong>{expenseActions.formatDate(expense.expense_date)}</strong>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-start">
                                    <div className="bg-light p-2 rounded me-3">
                                        <DollarSign size={20} className="text-danger" />
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Amount</small>
                                        <h4 className="mb-0 text-danger">
                                            {expenseActions.formatCurrency(expense.amount)}
                                        </h4>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Category */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-start">
                                    <div 
                                        className="p-2 rounded me-3"
                                        style={{ backgroundColor: (expense.category_color || '#6c757d') + '20' }}
                                    >
                                        <span style={{ color: expense.category_color || '#6c757d' }}>
                                            {getCategoryIcon(expense.category_icon)}
                                        </span>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Category</small>
                                        <strong>{expense.category_name || 'Uncategorized'}</strong>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Payment Method */}
                            <div className="col-md-6 mb-3">
                                <div className="d-flex align-items-start">
                                    <div className="bg-light p-2 rounded me-3">
                                        <CreditCard size={20} className="text-info" />
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Payment Method</small>
                                        <strong>
                                            {getPaymentMethodIcon(expense.payment_method)} {expense.payment_method?.replace('_', ' ')}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Reference No */}
                            {expense.reference_no && (
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <div className="bg-light p-2 rounded me-3">
                                            <Hash size={20} className="text-secondary" />
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Reference No</small>
                                            <strong>{expense.reference_no}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Paid To */}
                            {expense.paid_to && (
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <div className="bg-light p-2 rounded me-3">
                                            <User size={20} className="text-success" />
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Paid To</small>
                                            <strong>{expense.paid_to}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Bank Account */}
                            {expense.bank_account_name && (
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex align-items-start">
                                        <div className="bg-light p-2 rounded me-3">
                                            <Bookmark size={20} className="text-warning" />
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Bank Account</small>
                                            <strong>{expense.bank_name} - {expense.bank_account_name}</strong>
                                            <br />
                                            <small className="text-muted">{expense.account_number}</small>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Description */}
                            {expense.description && (
                                <div className="col-12 mb-3">
                                    <div className="d-flex align-items-start">
                                        <div className="bg-light p-2 rounded me-3">
                                            <FileText size={20} className="text-primary" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <small className="text-muted d-block">Description</small>
                                            <p className="mb-0">{expense.description}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Notes */}
                            {expense.notes && (
                                <div className="col-12 mb-3">
                                    <div className="d-flex align-items-start">
                                        <div className="bg-light p-2 rounded me-3">
                                            <FileText size={20} className="text-info" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <small className="text-muted d-block">Notes</small>
                                            <p className="mb-0">{expense.notes}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Bill Attachment */}
                            {expense.bill_attachment && (
                                <div className="col-12 mb-3">
                                    <div className="d-flex align-items-start">
                                        <div className="bg-light p-2 rounded me-3">
                                            <File size={20} className="text-danger" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <small className="text-muted d-block">Bill Attachment</small>
                                            <div className="d-flex align-items-center">
                                                <span className="me-3">
                                                    {expense.bill_attachment.split('/').pop()}
                                                </span>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={handleViewAttachment}
                                                >
                                                    <Eye size={14} className="me-1" />
                                                    View
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={handleDownloadAttachment}
                                                >
                                                    <Download size={14} className="me-1" />
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="card-footer bg-light">
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// PropTypes validation
ViewExpense.propTypes = {
    expense: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        expense_no: PropTypes.string,
        expense_date: PropTypes.string,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        payment_method: PropTypes.string,
        reference_no: PropTypes.string,
        paid_to: PropTypes.string,
        description: PropTypes.string,
        notes: PropTypes.string,
        bill_attachment: PropTypes.string,
        status: PropTypes.string,
        created_at: PropTypes.string,
        category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category_name: PropTypes.string,
        category_color: PropTypes.string,
        category_icon: PropTypes.string,
        bank_account_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bank_account_name: PropTypes.string,
        bank_name: PropTypes.string,
        account_number: PropTypes.string
    }).isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewExpense;